import { SourceDisplay as DataDisplaySource } from "@/components/DataDisplay";
import { INSTAGRAM_TITLE, InstagramTitle, SOURCE, Source } from "@/constants";
import { SOURCE_ICON } from "@/mappings/source_icon";
import { Episode } from "@/types/Episode";
import { cx } from "@/utils";
import Typography, { Variant } from "@components/Typography";
import { i18n } from "@lingui/core";

import styles from "./SourceDisplay.module.css";

const TV_NAME_MAPPING: Record<string, string> = {
  svtplay: "SVT Play",
  tv4play: "TV4 Play",
  nrk: "NRK TV",
  tv2no: "TV 2",
  dr_tv: "DRTV",
  tv2dk: "TV2",
};

const INSTAGRAM_TRANSLATIONS: Record<InstagramTitle | string, string> = {
  [INSTAGRAM_TITLE.IMAGE]: L.SOURCE_TYPE_INSTAGRAM,
  [INSTAGRAM_TITLE.STORY]: L.SOURCE_SUBTYPE_INSTAGRAM_STORY,
  [INSTAGRAM_TITLE.VIDEO]: L.SOURCE_SUBTYPE_INSTAGRAM_VIDEO,
};

/** Logos that are self explanatory, for example contains the source name, like "YouTube" */
const TEXT_LOGOS: Source[] = [SOURCE.YOUTUBE, SOURCE.TWITTER];

export type SourceDisplayType = {
  variant?: Variant;
  title?: string | InstagramTitle;
  noIcon?: boolean;
  className?: string;
} & Pick<Episode, "source" | "external_source">;

/**
 * SourceDisplay
 * Renders an Icon and a text based on a given source.
 */
const SourceDisplay: React.FunctionComponent<SourceDisplayType> = ({
  source,
  external_source,
  className,
  variant = "caption",
  title,
  noIcon = false,
  ...props
}) => {
  const isTextLogo = TEXT_LOGOS.includes(source);
  const Icon = !noIcon && SOURCE_ICON[source];
  const name =
    source === SOURCE.INSTAGRAM && title && INSTAGRAM_TRANSLATIONS[title] ? (
      i18n._(INSTAGRAM_TRANSLATIONS[title])
    ) : source === SOURCE.TV ? (
      TV_NAME_MAPPING[external_source] || <DataDisplaySource id={source} />
    ) : (
      <DataDisplaySource id={source} />
    );

  return (
    <span
      className={cx(styles.root, className)}
      {...props}
      data-testid="source-display"
    >
      {Icon ? <Icon height={16} className={source} /> : null}

      {(!isTextLogo || (isTextLogo && noIcon)) && (
        <Typography variant={variant} className={styles.source}>
          {name}
        </Typography>
      )}
    </span>
  );
};

export default SourceDisplay;
